import React, { useEffect, useState } from 'react';
import { PixelGlyph } from '../common/PixelGlyph';
import { FOOTER_DATA } from '../../data/ecoContent';

export const MinimalFooter: React.FC = () => {
  const [worldTimes, setWorldTimes] = useState({
    manaus: '',
    nairobi: '',
    svalbard: '',
    tokyo: '',
  });

  useEffect(() => {
    const updateTimes = () => {
      const now = new Date();
      setWorldTimes({
        manaus: now.toLocaleTimeString('en-US', { timeZone: 'America/Manaus', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        nairobi: now.toLocaleTimeString('en-US', { timeZone: 'Africa/Nairobi', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        svalbard: now.toLocaleTimeString('en-US', { timeZone: 'Europe/Oslo', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        tokyo: now.toLocaleTimeString('en-US', { timeZone: 'Asia/Tokyo', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      });
    };

    updateTimes();
    const interval = setInterval(updateTimes, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <footer className="relative bg-alabaster-200 border-t border-carbon-900/15 pt-16 sm:pt-24 pb-12 px-4 sm:px-6 z-10 font-mono text-xs text-carbon-900/70">
      <div className="max-w-7xl mx-auto space-y-12 sm:space-y-16">
        {/* Row 1: Global Planetary Biome Clocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 pb-10 border-b border-carbon-900/10">
          <div>
            <div className="text-[10px] text-carbon-900/50 tracking-wider uppercase mb-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-chlorophyll" />
              <span>MANAUS [AMAZONIA]</span>
            </div>
            <div className="text-base font-serif font-bold text-carbon-900">{worldTimes.manaus || '00:00:00'}</div>
            <div className="text-[9px] text-chlorophyll-deep uppercase">TROPICAL CANOPY</div>
          </div>

          <div>
            <div className="text-[10px] text-carbon-900/50 tracking-wider uppercase mb-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-solar-gold" />
              <span>NAIROBI [SAVANNA]</span>
            </div>
            <div className="text-base font-serif font-bold text-carbon-900">{worldTimes.nairobi || '00:00:00'}</div>
            <div className="text-[9px] text-solar-amber uppercase">SERENGETI CORRIDOR</div>
          </div>

          <div>
            <div className="text-[10px] text-carbon-900/50 tracking-wider uppercase mb-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-ozone-sky" />
              <span>SVALBARD [ARCTIC]</span>
            </div>
            <div className="text-base font-serif font-bold text-carbon-900">{worldTimes.svalbard || '00:00:00'}</div>
            <div className="text-[9px] text-ozone-deep uppercase">CRYOSPHERE VAULT</div>
          </div>

          <div>
            <div className="text-[10px] text-carbon-900/50 tracking-wider uppercase mb-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-chlorophyll" />
              <span>TOKYO [PACIFIC]</span>
            </div>
            <div className="text-base font-serif font-bold text-carbon-900">{worldTimes.tokyo || '00:00:00'}</div>
            <div className="text-[9px] text-chlorophyll-deep uppercase">BIO-TECH MATRIX</div>
          </div>
        </div>

        {/* Row 2: Mandatory Disclaimer Plaque */}
        <div className="p-6 border border-carbon-900/15 bg-white shadow-paper space-y-3">
          <div className="flex items-center gap-2 text-chlorophyll-deep font-bold tracking-widest text-xs uppercase font-mono">
            <span className="w-2 h-2 bg-chlorophyll inline-block" />
            <span>NOTICE & LEGAL DISCLAIMER // CONCEPT SPECIMEN</span>
          </div>
          <p className="text-xs sm:text-sm text-carbon-900/80 leading-relaxed font-sans">
            {FOOTER_DATA.disclaimer}
          </p>
          <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px] text-carbon-900/50 border-t border-carbon-900/10">
            <div>
              CRAFTED BY {FOOTER_DATA.creator.name} // BESPOKE DIGITAL ENGINEERING
            </div>
            <div>
              Demonstration specimen of Three.js procedural living biome, GSAP kinetic scroll, and museum-grade editorial UX.
            </div>
          </div>
        </div>

        {/* Row 3: Navigation, Brand & Direct Creator Contacts */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand Info */}
          <div className="md:col-span-5 space-y-4">
            <div className="flex items-center gap-3 text-carbon-900 font-serif font-extrabold text-xl tracking-wider">
              <div className="w-7 h-7 bg-carbon-900 text-alabaster-100 flex items-center justify-center font-serif text-sm font-bold">
                G
              </div>
              <span>GAIA PULSE // SPECIMEN 02</span>
            </div>
            <p className="text-xs text-carbon-900/70 max-w-sm leading-relaxed font-sans">
              High-end editorial living specimen engineered with procedural Three.js living biome kinematics, classical Swiss typography, and zero layout shift.
            </p>
            <div className="text-[11px] text-chlorophyll-deep font-mono flex items-center gap-2 pt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-chlorophyll" />
              <span>CLUSTER HOST: eco.th157le.ru</span>
            </div>
          </div>

          {/* Exhibition Links */}
          <div className="md:col-span-3 space-y-3">
            <div className="text-[10px] tracking-widest uppercase text-carbon-900/50 mb-2">
              EXHIBITION TRACK
            </div>
            <ul className="space-y-2 text-carbon-900/80">
              <li>
                <a href="#hero" className="hover:text-chlorophyll-deep transition-colors">
                  01. Living Biome Core
                </a>
              </li>
              <li>
                <a href="#manifesto" className="hover:text-chlorophyll-deep transition-colors">
                  02. Archival Manifesto
                </a>
              </li>
              <li>
                <a href="#pillars" className="hover:text-chlorophyll-deep transition-colors">
                  03. Four Continental Pillars
                </a>
              </li>
              <li>
                <a href="#architecture" className="hover:text-chlorophyll-deep transition-colors">
                  04. Biosphere OS Codex
                </a>
              </li>
              <li>
                <a href="#simulator" className="hover:text-chlorophyll-deep transition-colors">
                  05. Carbon Drawdown Laboratory
                </a>
              </li>
              <li>
                <a href="#benchmarks" className="hover:text-chlorophyll-deep transition-colors">
                  06. Thermodynamic Benchmarks
                </a>
              </li>
            </ul>
          </div>

          {/* Creator Micro-Advertising & Links */}
          <div className="md:col-span-4 space-y-3">
            <div className="text-[10px] tracking-widest uppercase text-chlorophyll-deep font-bold mb-2">
              CREATOR LINKS & COMMISSIONS
            </div>
            <ul className="space-y-2.5">
              <li>
                <a
                  href={FOOTER_DATA.creator.telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-chlorophyll-deep transition-colors flex items-center justify-between group py-0.5"
                  data-cursor="interactive"
                >
                  <span className="text-carbon-900 group-hover:text-chlorophyll-deep font-medium">
                    Telegram ({FOOTER_DATA.creator.telegram})
                  </span>
                  <PixelGlyph name="arrow-up-right" size={12} color="#059669" />
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${FOOTER_DATA.creator.email}`}
                  className="hover:text-chlorophyll-deep transition-colors flex items-center justify-between group py-0.5"
                  data-cursor="interactive"
                >
                  <span className="text-carbon-900 group-hover:text-chlorophyll-deep font-medium">
                    Email ({FOOTER_DATA.creator.email})
                  </span>
                  <PixelGlyph name="arrow-up-right" size={12} color="#059669" />
                </a>
              </li>
              <li>
                <a
                  href={FOOTER_DATA.creator.showcaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-chlorophyll-deep transition-colors flex items-center justify-between group py-0.5"
                  data-cursor="interactive"
                >
                  <span className="text-carbon-900 group-hover:text-chlorophyll-deep font-medium">
                    Specimen 01 ({FOOTER_DATA.creator.showcaseDomain})
                  </span>
                  <PixelGlyph name="arrow-up-right" size={12} color="#059669" />
                </a>
              </li>
              <li>
                <a
                  href={FOOTER_DATA.creator.blogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-chlorophyll-deep transition-colors flex items-center justify-between group py-0.5"
                  data-cursor="interactive"
                >
                  <span className="text-carbon-900 group-hover:text-chlorophyll-deep font-medium">
                    Personal Blog ({FOOTER_DATA.creator.blogDomain})
                  </span>
                  <PixelGlyph name="arrow-up-right" size={12} color="#059669" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Row 4: System Rights */}
        <div className="pt-8 border-t border-carbon-900/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-carbon-900/50">
          <div>
            © {new Date().getFullYear()} {FOOTER_DATA.creator.name}. ALL RIGHTS RESERVED // SPECIMEN 02.
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <span>PORCELAIN ALABASTER</span>
            <span>•</span>
            <span>THREE.JS LIVING BIOME</span>
            <span>•</span>
            <span>LENIS 1.1</span>
            <span>•</span>
            <span>GSAP KINETICS</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
